import { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import { DatePicker, Form, Input, message, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";

import { Loader } from "../../../../common/components/Loader/Loader";
import { FormProvider } from "../../../../common/provider/form/Form.provider";
import { SchedulesInputs } from "../../work_calendar/components/workShedule/schedules/SchedulesInputs";
import { CustomButton } from "../../../../common/components/CustomButton/CustomButton";
import { GlobalRequest } from "../../../../common/services/request";

import { SchedulesService } from "../core/_request";

import style from "../style.module.scss";
import { CustomPopConfirm } from "../../../../common/components/PopConfirm/CustomPopConfirm";
import { RiDeleteBin5Line } from "react-icons/ri";

const { RangePicker } = DatePicker;

interface MainSchedulesProps {
  currentType: string;
}

interface ScheduleTime {
  "@id"?: string;
  id?: number;
  startTime: string | null;
  endTime: string | null;
  rate?: string | number | null;
}

interface ScheduleDay {
  day: {
    "@id": string;
    id: number | string;
    name: string;
  };
  times: ScheduleTime[];
}

interface CadenceItem {
  "@id"?: string;
  id?: number;

  name: string;

  validFrom: string | null;
  validTo: string | null;

  cadenceRules?: any[];
  cadenceRulesArray?: ScheduleDay[];

  isDraft?: boolean;
  draftKey?: string;

  editorSchedules?: ScheduleDay[];
}

/**
 * Copie profonde des horaires.
 */
const cloneScheduleDays = (days: ScheduleDay[]): ScheduleDay[] =>
  days.map((item) => ({
    day: typeof item.day === "object" ? { ...item.day } : item.day,

    times: (item.times ?? []).map((time) => ({
      ...time,
    })),
  }));

const getResourceId = (
  resource:
    | {
        id?: number;
        "@id"?: string;
      }
    | null
    | undefined
): number | undefined => {
  if (resource?.id) {
    return resource.id;
  }

  if (!resource?.["@id"]) {
    return undefined;
  }

  const lastPart = resource["@id"].split("/").filter(Boolean).pop();

  const parsedId = Number(lastPart);

  return Number.isNaN(parsedId) ? undefined : parsedId;
};

const getCadenceKey = (cadence: CadenceItem): string =>
  cadence.draftKey ?? cadence["@id"] ?? String(cadence.id);

const parseDate = (date?: string | null) => {
  if (!date) {
    return null;
  }

  const ddmmyyyy = dayjs(date, "DD/MM/YYYY", true);

  if (ddmmyyyy.isValid()) {
    return ddmmyyyy;
  }

  const iso = dayjs(date);

  return iso.isValid() ? iso : null;
};

const formatDate = (date?: string | null): string => {
  const parsed = parseDate(date);

  return parsed ? parsed.format("DD/MM/YYYY") : "";
};

/**
 * Transforme le détail API dans le format
 * attendu par SchedulesInputs.
 */
const convertCadenceToEditorValue = (
  cadence: any,
  defaultDays: ScheduleDay[]
): ScheduleDay[] => {
  const groupedRules =
    cadence?.cadenceRulesArray ?? cadence?.scheduleRulesArray;

  /**
   * L'API renvoie déjà :
   *
   * [
   *   {
   *     day: {...},
   *     times: [...]
   *   }
   * ]
   */
  if (
    Array.isArray(groupedRules) &&
    groupedRules.every((item) => Array.isArray(item?.times))
  ) {
    return cloneScheduleDays(groupedRules);
  }

  /**
   * Fallback si l'API renvoie cadenceRules
   * sous forme plate.
   */
  const flatRules = cadence?.cadenceRules ?? cadence?.rules ?? [];

  return defaultDays.map((defaultDay) => {
    const defaultDayIri = defaultDay.day?.["@id"];

    const defaultDayId = defaultDay.day?.id;

    const matchingRules = flatRules.filter((rule: any) => {
      const ruleDayIri =
        typeof rule.day === "string" ? rule.day : rule.day?.["@id"];

      const ruleDayId = typeof rule.day === "object" ? rule.day?.id : undefined;

      return (
        ruleDayIri === defaultDayIri ||
        String(ruleDayId) === String(defaultDayId)
      );
    });

    return {
      day: { ...defaultDay.day },

      times:
        matchingRules.length > 0
          ? matchingRules.map((rule: any) => ({
              "@id": rule["@id"],
              id: rule.id,
              startTime: rule.startTime,
              endTime: rule.endTime,
              rate: rule.rate ?? null,
            }))
          : [
              {
                startTime: null,
                endTime: null,
                rate: null,
              },
            ],
    };
  });
};

export const MainCadence = ({ currentType }: MainSchedulesProps) => {
  const [form] = Form.useForm();

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [isOpenConfirm, setIsOpenConfirm] = useState(false);

  /**
   * Liste des cadences à gauche.
   */
  const [cadenceList, setCadenceList] = useState<CadenceItem[]>([]);

  /**
   * Cadence actuellement sélectionnée.
   */
  const [activeCadence, setActiveCadence] = useState<CadenceItem | null>(null);

  /**
   * Schedules affichés dans
   * SchedulesInputs.
   */
  const [schedules, setSchedules] = useState<ScheduleDay[]>([]);

  /**
   * Valeurs par défaut pour
   * une NOUVELLE cadence.
   */
  const [defaultWorkSchedules, setDefaultWorkSchedules] = useState<
    ScheduleDay[]
  >([]);

  /**
   * Valeurs de la cadence actuellement
   * affichée dans l'éditeur.
   */
  const [cadenceName, setCadenceName] = useState("");

  const [validity, setValidity] = useState<{
    validFrom: string | null;
    validTo: string | null;
  }>({
    validFrom: null,
    validTo: null,
  });

  /**
   * IMPORTANT :
   * valeurs indépendantes utilisées
   * uniquement dans le formulaire
   * "Ajouter une cadence".
   */
  const [newCadenceName, setNewCadenceName] = useState("");

  const [newValidity, setNewValidity] = useState<{
    validFrom: string | null;
    validTo: string | null;
  }>({
    validFrom: null,
    validTo: null,
  });

  const [isLoading, setIsLoading] = useState(false);

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const [isChanged, setIsChanged] = useState(false);

  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    if (currentType === "main") {
      void Promise.all([getWeekLists(), getNormalCadencesList()]);
    }
  }, [currentType]);

  const handleClosePopConfirm = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    setIsOpenConfirm(false);
    setSelectedId(null);
  };

  const handleClickOnDeleteCadence = (
    e: React.MouseEvent,
    cadence: CadenceItem
  ) => {
    e.stopPropagation();

    /**
     * Draft => suppression locale immédiate
     */
    if (cadence.isDraft) {
      const cadenceKey = getCadenceKey(cadence);

      setCadenceList((previous) =>
        previous.filter((item) => getCadenceKey(item) !== cadenceKey)
      );

      if (activeCadence && getCadenceKey(activeCadence) === cadenceKey) {
        setActiveCadence(null);
        setSchedules([]);
        setCadenceName("");

        setValidity({
          validFrom: null,
          validTo: null,
        });

        setIsChanged(false);
      }

      return;
    }

    /**
     * Cadence enregistrée
     */
    const cadenceId = getResourceId(cadence);

    if (!cadenceId) {
      message.error("L'identifiant de cette cadence est introuvable.");

      return;
    }

    setSelectedId(cadenceId);
    setIsOpenConfirm(true);
  };

  const handleConfirmDeleteCadence = async (e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (selectedId === null) {
      return;
    }

    try {
      setIsLoading(true);

      const res = await SchedulesService.deleteCadence(selectedId);

      const isDeleted =
        res?.status === HttpStatusCode.Ok ||
        res?.status === HttpStatusCode.NoContent;

      if (!isDeleted) {
        message.error("Impossible de supprimer la cadence.");

        return;
      }

      /**
       * Retire immédiatement la cadence
       * de la liste locale.
       */
      setCadenceList((previous) =>
        previous.filter((cadence) => getResourceId(cadence) !== selectedId)
      );

      /**
       * Si on supprime la cadence actuellement
       * ouverte, on vide l'éditeur.
       */
      if (getResourceId(activeCadence) === selectedId) {
        setActiveCadence(null);

        setSchedules([]);

        setCadenceName("");

        setValidity({
          validFrom: null,
          validTo: null,
        });

        setIsChanged(false);
      }

      message.success("La cadence a été supprimée.");
    } catch (error) {
      console.error(error);

      message.error("Impossible de supprimer la cadence.");
    } finally {
      setIsLoading(false);
      handleClosePopConfirm();
    }
  };

  /**
   * Charge les jours et prépare les valeurs
   * par défaut pour une nouvelle cadence.
   *
   * Même fonctionnement que
   * CustomizedSchedules.
   */
  const getWeekLists = async () => {
    try {
      const res = await GlobalRequest.get("/api/week_days/select");

      if (res?.status !== HttpStatusCode.Ok) {
        return;
      }

      const days = res.data?.member ?? [];

      const defaultSchedules: ScheduleDay[] = days.map((day: any) => ({
        day,

        times: [
          {
            startTime: "07:00",
            endTime: "15:00",
            rate: null,
          },
        ],
      }));

      setDefaultWorkSchedules(defaultSchedules);
    } catch (error) {
      console.error(error);

      message.error("Impossible de charger les jours de la semaine.");
    }
  };

  /**
   * Charge la liste des cadences normales.
   */
  const getNormalCadencesList = async (): Promise<CadenceItem[]> => {
    try {
      setIsLoading(true);

      const res = await SchedulesService.getNormalCadencesList();

      if (res?.status !== HttpStatusCode.Ok) {
        return [];
      }

      const cadences: CadenceItem[] =
        res.data?.member ?? res.data?.cadences ?? [];

      setCadenceList(cadences);

      return cadences;
    } catch (error) {
      console.error(error);

      message.error("Impossible de charger les cadences.");

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Charge le détail d'une cadence
   * existante.
   */
  const getCadence = async (cadence: CadenceItem) => {
    const cadenceId = getResourceId(cadence);

    if (!cadenceId) {
      message.error("L'identifiant de cette cadence est introuvable.");

      return;
    }

    try {
      setIsLoading(true);

      const res = await SchedulesService.getCadence(cadenceId);

      if (res?.status !== HttpStatusCode.Ok) {
        return;
      }

      const detailedCadence: CadenceItem = {
        ...cadence,
        ...res.data,
        isDraft: false,
      };

      setActiveCadence(detailedCadence);

      setCadenceName(detailedCadence.name ?? "");

      setValidity({
        validFrom: detailedCadence.validFrom ?? null,

        validTo: detailedCadence.validTo ?? null,
      });

      setSchedules(convertCadenceToEditorValue(res.data, defaultWorkSchedules));

      setIsChanged(false);
      setShowInput(false);
    } catch (error) {
      console.error(error);

      message.error("Impossible de charger la cadence.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clique sur une cadence à gauche.
   */
  const handleSelectCadence = (cadence: CadenceItem) => {
    if (cadence.isDraft) {
      setActiveCadence(cadence);

      setCadenceName(cadence.name);

      setValidity({
        validFrom: cadence.validFrom,

        validTo: cadence.validTo,
      });

      setSchedules(
        cloneScheduleDays(cadence.editorSchedules ?? defaultWorkSchedules)
      );

      /**
       * Le draft doit pouvoir être
       * enregistré immédiatement.
       */
      setIsChanged(true);

      setShowInput(false);

      return;
    }

    void getCadence(cadence);
  };

  /**
   * Affiche le formulaire d'ajout.
   *
   * IMPORTANT :
   * on ne touche PLUS à schedules,
   * cadenceName ou validity.
   */
  const handleShowInput = () => {
    const existingDraft = cadenceList.find((cadence) => cadence.isDraft);

    if (existingDraft) {
      handleSelectCadence(existingDraft);
      return;
    }

    setNewCadenceName("");

    setNewValidity({
      validFrom: null,
      validTo: null,
    });

    setShowInput(true);
  };

  const handleCancelCadenceAddition = () => {
    setNewCadenceName("");

    setNewValidity({
      validFrom: null,
      validTo: null,
    });

    setShowInput(false);
  };

  /**
   * Clique sur Continuer.
   *
   * Création d'un draft local.
   */
  const handleAdd = () => {
    if (!newCadenceName.trim()) {
      message.warning("Veuillez renseigner le nom de la cadence.");

      return;
    }

    if (!newValidity.validFrom || !newValidity.validTo) {
      message.warning("Veuillez renseigner la période de validation.");

      return;
    }

    if (defaultWorkSchedules.length === 0) {
      message.warning("Les horaires par défaut ne sont pas encore chargés.");

      return;
    }

    /**
     * Comme CustomizedSchedules :
     * un seul draft à la fois.
     */
    const existingDraft = cadenceList.find((cadence) => cadence.isDraft);

    if (existingDraft) {
      setActiveCadence(existingDraft);

      setCadenceName(existingDraft.name);

      setValidity({
        validFrom: existingDraft.validFrom,

        validTo: existingDraft.validTo,
      });

      setSchedules(
        cloneScheduleDays(existingDraft.editorSchedules ?? defaultWorkSchedules)
      );

      setIsChanged(true);
      setShowInput(false);

      return;
    }

    /**
     * IMPORTANT :
     * on prend les DEFAULTS,
     * pas schedules.
     */
    const draftSchedules = cloneScheduleDays(defaultWorkSchedules);

    const draft: CadenceItem = {
      draftKey: `draft-${Date.now()}`,

      name: newCadenceName.trim(),

      validFrom: newValidity.validFrom,

      validTo: newValidity.validTo,

      isDraft: true,

      editorSchedules: draftSchedules,
    };

    setCadenceList((previous) => [
      ...previous.filter((item) => !item.isDraft),
      draft,
    ]);

    setActiveCadence(draft);

    /**
     * envoyer à l'editeur
     * les horaires 07:00 - 15:00.
     */
    setSchedules(draftSchedules);

    setCadenceName(draft.name);

    setValidity({
      validFrom: draft.validFrom,

      validTo: draft.validTo,
    });

    /**
     * Enregistrer immédiatement actif.
     */
    setIsChanged(true);

    setShowInput(false);
  };

  /**
   * Modification de SchedulesInputs.
   */
  const handleSchedulesChange = (newSchedules: ScheduleDay[]) => {
    setSchedules(newSchedules);

    setIsChanged(true);

    if (!activeCadence?.isDraft) {
      return;
    }

    const activeKey = getCadenceKey(activeCadence);

    setActiveCadence((previous) =>
      previous
        ? {
            ...previous,
            editorSchedules: newSchedules,
          }
        : previous
    );

    setCadenceList((previous) =>
      previous.map((cadence) =>
        getCadenceKey(cadence) === activeKey
          ? {
              ...cadence,
              editorSchedules: newSchedules,
            }
          : cadence
      )
    );
  };

  /**
   * Mise à jour dynamique des informations du draft
   * quand la période change.
   */
  const updateDraftMetadata = (data: Partial<CadenceItem>) => {
    if (!activeCadence?.isDraft) {
      return;
    }

    const activeKey = getCadenceKey(activeCadence);

    setActiveCadence((previous) =>
      previous
        ? {
            ...previous,
            ...data,
          }
        : previous
    );

    setCadenceList((previous) =>
      previous.map((cadence) =>
        getCadenceKey(cadence) === activeKey
          ? {
              ...cadence,
              ...data,
            }
          : cadence
      )
    );
  };

  /**
   * Payload cadenceRules.
   */
  const createRulesPayload = () =>
    schedules.flatMap((day) =>
      (day.times ?? [])
        .filter((time) => Boolean(time.startTime) && Boolean(time.endTime))
        .map((time) => {
          const item: {
            "@id"?: string;
            day: string;
            startTime: string;
            endTime: string;
            rate?: string;
          } = {
            day: day.day["@id"],

            startTime: time.startTime as string,

            endTime: time.endTime as string,
          };

          if (
            time.rate !== null &&
            time.rate !== undefined &&
            time.rate !== ""
          ) {
            item.rate = String(time.rate);
          }

          /**
           * @id seulement sur
           * une règle existante.
           */
          if (time["@id"]) {
            item["@id"] = time["@id"];
          }

          return item;
        })
    );

  /**
   * POST / PATCH
   */
  const handleSubmit = async () => {
    if (!activeCadence) {
      return;
    }

    if (!cadenceName.trim()) {
      message.warning("Veuillez renseigner le nom de la cadence.");

      return;
    }

    if (!validity.validFrom || !validity.validTo) {
      message.warning("Veuillez renseigner la période de validation.");

      return;
    }

    const hasIncompleteTime = schedules.some((day) =>
      day.times.some((time) => {
        const hasStart = Boolean(time.startTime);

        const hasEnd = Boolean(time.endTime);

        return hasStart !== hasEnd;
      })
    );

    if (hasIncompleteTime) {
      message.warning("Certaines plages horaires sont incomplètes.");

      return;
    }

    const cadenceRules = createRulesPayload();

    const payload = {
      //"@type": "Cadence",
      type: "api/schedule_types/NORMAL",

      name: cadenceName.trim(),

      validFrom: validity.validFrom,

      validTo: validity.validTo,

      cadenceRules,
    };

    try {
      setIsLoadingSubmit(true);

      /**
       * ========================
       * CREATE
       * ========================
       */
      if (activeCadence.isDraft) {
        const res = await SchedulesService.postCadence(payload);

        const isCreated =
          res?.status === HttpStatusCode.Created ||
          res?.status === HttpStatusCode.Ok;

        if (!isCreated) {
          return;
        }

        message.success("La cadence a été créée.");

        const draftKey = activeCadence.draftKey;

        setCadenceList((previous) =>
          previous.filter((cadence) => cadence.draftKey !== draftKey)
        );

        const refreshedCadences = await getNormalCadencesList();

        const createdId = getResourceId(res.data);

        if (createdId) {
          const createdCadence =
            refreshedCadences.find(
              (cadence) => getResourceId(cadence) === createdId
            ) ?? res.data;

          await getCadence(createdCadence);
        } else {
          setActiveCadence(null);

          setSchedules([]);

          setCadenceName("");

          setValidity({
            validFrom: null,
            validTo: null,
          });
        }

        setNewCadenceName("");

        setNewValidity({
          validFrom: null,
          validTo: null,
        });

        setIsChanged(false);

        return;
      }

      /**
       * ========================
       * PATCH
       * ========================
       */
      const cadenceId = getResourceId(activeCadence);

      if (!cadenceId) {
        message.error("L'identifiant de cette cadence est introuvable.");

        return;
      }

      const dataToPatch = {
        id: cadenceId,
        ...payload,
      };

      const res = await SchedulesService.patchCadence(dataToPatch);

      const isUpdated =
        res?.status === HttpStatusCode.Ok ||
        res?.status === HttpStatusCode.NoContent;

      if (!isUpdated) {
        return;
      }

      message.success("La cadence a été modifiée.");

      setIsChanged(false);

      await getNormalCadencesList();

      await getCadence({
        ...activeCadence,

        name: cadenceName,

        validFrom: validity.validFrom,

        validTo: validity.validTo,
      });
    } catch (error) {
      console.error(error);

      message.error(
        activeCadence.isDraft
          ? "Impossible de créer la cadence."
          : "Impossible de modifier la cadence."
      );
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />

      <div className={style.customizedSchedules__containers}>
        {/* ===================== */}
        {/* LISTE À GAUCHE */}
        {/* ===================== */}

        <div className={style.customizedSchedules__primary_container}>
          <div className={style.customizedSchedules__clients_list}>
            {cadenceList.map((cadence) => {
              const cadenceKey = getCadenceKey(cadence);

              const isActive = Boolean(
                activeCadence && getCadenceKey(activeCadence) === cadenceKey
              );

              return (
                <div
                  key={cadenceKey}
                  onClick={() => handleSelectCadence(cadence)}
                  className={`${style.customizedSchedules__client_item} ${
                    isActive
                      ? style["customizedSchedules__client_item--active"]
                      : ""
                  }`}
                  aria-pressed={isActive}
                >
                  <div className={style.customizedSchedules__client_name}>
                    <div>{cadence.name}</div>

                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.7,
                        marginTop: 3,
                      }}
                    >
                      {formatDate(cadence.validFrom)}

                      {" - "}

                      {formatDate(cadence.validTo)}
                    </div>
                  </div>
                  <CustomPopConfirm
                    title="Voulez-vous vraiment supprimer cette cadence ?"
                    okText="Oui"
                    cancelText="Non"
                    isOpen={
                      isOpenConfirm && selectedId === getResourceId(cadence)
                    }
                    onClose={handleClosePopConfirm}
                    onConfirm={handleConfirmDeleteCadence}
                  >
                    <div
                      className={`${
                        style.customizedSchedules__customer_delete
                      } ${
                        isOpenConfirm && selectedId === getResourceId(cadence)
                          ? style[
                              "customizedSchedules__customer_delete--active"
                            ]
                          : ""
                      }`}
                      onClick={(e) => handleClickOnDeleteCadence(e, cadence)}
                    >
                      <RiDeleteBin5Line
                        title="supprimer"
                        className={
                          style.customizedSchedules__customer_delete_icon
                        }
                      />
                    </div>
                  </CustomPopConfirm>
                </div>
              );
            })}

            {!isLoading && cadenceList.length === 0 && (
              <div className={style.customizedSchedules__empty_list}>
                Aucune cadence.
              </div>
            )}
          </div>

          {/* ===================== */}
          {/* AJOUT */}
          {/* ===================== */}

          {!showInput ? (
            <>
              {activeCadence?.isDraft ? (
                <span className={style.customizedSchedules__indication}>
                  Cliquez sur « Enregistrer » pour sauvegarder les
                  modifications.
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleShowInput}
                  className={style.customizedSchedules__add_btn}
                >
                  Ajouter une cadence
                </button>
              )}
            </>
          ) : (
            <div className={style.customizedSchedules__customer_top_section}>
              <div
                style={{
                  width: "100%",
                }}
              >
                <Input
                  placeholder="Nom de la cadence"
                  value={newCadenceName}
                  onChange={(e) => setNewCadenceName(e.target.value)}
                />

                <RangePicker
                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}
                  format="DD/MM/YYYY"
                  value={[
                    parseDate(newValidity.validFrom),

                    parseDate(newValidity.validTo),
                  ]}
                  onChange={(dates) => {
                    setNewValidity({
                      validFrom: dates?.[0]?.format("DD/MM/YYYY") ?? null,

                      validTo: dates?.[1]?.format("DD/MM/YYYY") ?? null,
                    });
                  }}
                />
              </div>

              <button
                type="button"
                className={style.customizedSchedules__add_btn}
                disabled={
                  !newCadenceName.trim() ||
                  !newValidity.validFrom ||
                  !newValidity.validTo ||
                  defaultWorkSchedules.length === 0
                }
                onClick={handleAdd}
              >
                Continuer
              </button>

              <button
                type="button"
                onClick={handleCancelCadenceAddition}
                className={style.customizedSchedules__cancel_btn}
              >
                Annuler
              </button>
            </div>
          )}
        </div>

        {/* ===================== */}
        {/* EDITEUR À DROITE */}
        {/* ===================== */}

        <div className={style.customizedSchedules__secondary_container}>
          {isLoading && activeCadence && <Skeleton active />}

          {!isLoading && activeCadence && (
            <FormProvider
              Form={Form}
              form={form}
              debug
              onFinish={handleSubmit}
              onFinishFailed={(error) => {
                console.error(error);
              }}
            >
              {/* NOM - UNIQUEMENT CADENCE EXISTANTE */}
              {!activeCadence.isDraft && (
                <div className={style.schedulesInputs}>
                  <span className={style.schedulesInputs__label}>Nom :</span>

                  <Input
                    value={cadenceName}
                    onChange={(e) => {
                      setCadenceName(e.target.value);

                      setIsChanged(true);
                    }}
                  />
                </div>
              )}

              {/* PÉRIODE */}
              <div className={style.schedulesInputs}>
                <span className={style.schedulesInputs__label}>
                  Période de validation :
                </span>

                <RangePicker
                  className={style.schedulesInputs__picker}
                  format="DD/MM/YYYY"
                  value={[
                    parseDate(validity.validFrom),

                    parseDate(validity.validTo),
                  ]}
                  onChange={(dates) => {
                    const newValidity = {
                      validFrom: dates?.[0]?.format("DD/MM/YYYY") ?? null,

                      validTo: dates?.[1]?.format("DD/MM/YYYY") ?? null,
                    };

                    setValidity(newValidity);

                    updateDraftMetadata(newValidity);

                    setIsChanged(true);
                  }}
                />
              </div>

              {/* HORAIRES */}
              <SchedulesInputs
                value={schedules}
                onChange={handleSchedulesChange}
                onChanged={() => setIsChanged(true)}
                rate
              />

              {!validity.validFrom || !validity.validTo ? (
                <p
                  style={{
                    color: "red",
                  }}
                >
                  Veuillez renseigner la période de validation.
                </p>
              ) : null}

              <div className="mt-4">
                <CustomButton
                  type="submit"
                  variant="primary"
                  disabled={
                    !isChanged ||
                    !cadenceName.trim() ||
                    !validity.validFrom ||
                    !validity.validTo
                  }
                  isLoading={isLoadingSubmit}
                >
                  Enregistrer
                </CustomButton>
              </div>
            </FormProvider>
          )}
        </div>
      </div>
    </>
  );
};
